import { useState, useCallback, useMemo } from 'react';
import { Modal, Typography, Tag, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

const { Text } = Typography;

export interface NowcastingViewerTile {
  url: string;
  label: string;
  isNowcast: boolean;
  forecastOffset?: number;
  source?: 'radar' | 'l2';
}

interface NowcastingTileViewerProps {
  open: boolean;
  onClose: () => void;
  tiles: NowcastingViewerTile[];
  loading?: boolean;
  title?: string;
}

interface TileGroup {
  key: string;
  label: string;
  color: string;
  tagColor: string;
  tiles: NowcastingViewerTile[];
}

const SOURCE_CONFIG: Record<string, { label: string; color: string; tagColor: string }> = {
  original: { label: 'Original', color: 'rgba(82,196,26,0.15)', tagColor: 'green' },
  radar: { label: 'Radar (old)', color: 'rgba(250,173,20,0.1)', tagColor: 'orange' },
  l2: { label: 'L2', color: 'rgba(114,46,209,0.1)', tagColor: 'purple' },
};

export const NowcastingTileViewer = ({
  open,
  onClose,
  tiles,
  loading = false,
  title = 'Nowcasting Tile Viewer',
}: NowcastingTileViewerProps) => {
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const handleImageError = useCallback((url: string) => {
    setFailedImages(prev => new Set(prev).add(url));
  }, []);

  const groups = useMemo((): TileGroup[] => {
    const result: TileGroup[] = [];

    const originals = tiles.filter(t => !t.isNowcast);
    if (originals.length > 0) {
      result.push({
        key: 'original',
        ...SOURCE_CONFIG.original,
        tiles: originals,
      });
    }

    const radarTiles = tiles.filter(t => t.isNowcast && t.source === 'radar');
    if (radarTiles.length > 0) {
      result.push({
        key: 'radar',
        ...SOURCE_CONFIG.radar,
        tiles: radarTiles,
      });
    }

    const l2Tiles = tiles.filter(t => t.isNowcast && t.source === 'l2');
    if (l2Tiles.length > 0) {
      result.push({
        key: 'l2',
        ...SOURCE_CONFIG.l2,
        tiles: l2Tiles,
      });
    }

    const otherTiles = tiles.filter(t => t.isNowcast && !t.source);
    if (otherTiles.length > 0) {
      result.push({
        key: 'nowcast',
        label: 'Nowcast',
        color: 'rgba(22,119,255,0.1)',
        tagColor: 'blue',
        tiles: otherTiles,
      });
    }

    return result;
  }, [tiles]);

  if (tiles.length === 0) return null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>{title}</span>
          {loading && <Spin indicator={<LoadingOutlined spin />} size="small" />}
          {!loading && (
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
              ({tiles.length} tile{tiles.length !== 1 ? 's' : ''})
            </Text>
          )}
        </div>
      }
      footer={null}
      width="90vw"
      style={{ maxWidth: 1400, top: 24 }}
      styles={{ body: { maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' } }}
      destroyOnHidden
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '4px 0' }}>
        {groups.map(group => (
          <div key={group.key}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 8,
            }}>
              <Tag color={group.tagColor} style={{ margin: 0, fontSize: 12 }}>{group.label}</Tag>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {group.tiles.length} tile{group.tiles.length !== 1 ? 's' : ''}
              </Text>
            </div>

            <div style={{
              display: 'flex',
              gap: 8,
              overflowX: 'auto',
              paddingBottom: 4,
            }}>
              {group.tiles.map(tile => (
                <div key={tile.url} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 6,
                  overflow: 'hidden',
                  border: `1px solid rgba(128,128,128,0.2)`,
                  background: group.color,
                  flexShrink: 0,
                  width: 200,
                }}>
                  <div style={{
                    backgroundColor: '#000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 160,
                  }}>
                    {failedImages.has(tile.url) ? (
                      <Text type="danger" style={{ fontSize: 11 }}>Failed to load</Text>
                    ) : (
                      <img
                        src={tile.url}
                        alt={tile.label}
                        onError={() => handleImageError(tile.url)}
                        style={{
                          width: '100%',
                          height: '100%',
                          display: 'block',
                          objectFit: 'contain',
                        }}
                      />
                    )}
                  </div>

                  <div style={{
                    padding: '4px 6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 4,
                  }}>
                    <Text style={{ fontSize: 11 }}>{tile.label}</Text>
                    {tile.isNowcast && (
                      <Tag color="blue" style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>
                        +{tile.forecastOffset}min
                      </Tag>
                    )}
                    {!tile.isNowcast && (
                      <Tag color="green" style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>
                        Obs
                      </Tag>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
};
